import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { deleteTransaction } from "@/lib/queries";
import { createTransaction, type TxHeaderInput, type TxLineInput } from "@/lib/transactions";

/** Subpestañas de "Ingresos", en el search param `tab`. Un valor inválido cae en `pozo`. */
export const INCOME_TABS = ["pozo", "detalle"] as const;
export type IncomeTab = (typeof INCOME_TABS)[number];
export const incomeSearchSchema = z.object({
  tab: fallback(z.enum(INCOME_TABS), "pozo").default("pozo"),
});
export const incomeSearchValidator = zodValidator(incomeSearchSchema);

export interface IncomeRecord {
  id: string;
  household_id: string;
  member_id: string;
  amount: number;
  currency: string;
  fx_rate_to_base: number;
  amount_base: number;
  base_currency: string;
  period_year: number;
  period_month: number;
  source_type: "manual" | "migrated_transaction";
  source_transaction_id: string | null;
  /** F5b: parte declarada del pozo de ingresos de esta cuenta del hogar (sin movimiento propio). */
  pool_account_id: string | null;
  notes: string | null;
  created_at: string;
}

export interface HouseholdSplitRatio {
  split_ratio_a: number | null;
  split_ratio_b: number | null;
  split_ratio_updated_at: string | null;
  income_window_months: number;
  member_a_id: string | null;
  member_b_id: string | null;
}

export async function fetchIncomeRecords(householdId: string): Promise<IncomeRecord[]> {
  const { data, error } = await supabase
    .from("income_records" as any)
    .select("*")
    .eq("household_id", householdId)
    .order("period_year", { ascending: false })
    .order("period_month", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as IncomeRecord[];
}

export interface CreateIncomeRecordInput {
  household_id: string;
  member_id: string;
  amount: number;
  currency: string;
  fx_rate_to_base: number;
  amount_base: number;
  base_currency: string;
  period_year: number;
  period_month: number;
  source_transaction_id?: string;
  pool_account_id?: string;
  notes?: string;
}

export async function createIncomeRecord(payload: CreateIncomeRecordInput): Promise<IncomeRecord> {
  const { data, error } = await supabase
    .from("income_records" as any)
    .insert({ ...payload, source_type: "manual" })
    .select()
    .single();
  if (error) throw error;
  return data as unknown as IncomeRecord;
}

export interface MyIncomeInput {
  householdId: string;
  memberId: string;
  accountId: string;
  /** Categoría de tipo `income`. Sin ella el extracto lee el depósito como un gasto. */
  categoryId: string;
  /** yyyy-mm-dd */
  date: string;
  amount: number;
  /** Moneda de la cuenta que recibe. */
  currency: string;
  baseCurrency: string;
  /** Tasa matemática: amount * fxRateToBase = amount_base. 1 si la cuenta ya está en la moneda base. */
  fxRateToBase: number;
  /** Descripción del movimiento (header). El otro miembro no la ve: el header queda `private`. */
  notes?: string;
  /**
   * Nota que se guarda en `income_records.notes` — el OTRO miembro SÍ la lee (`fetchIncomeRecords`
   * no filtra por owner). Sin este campo, se usa `notes` (comportamiento de `IncomeDialog`: el
   * usuario la tipeó a propósito para compartirla). Pasar `""` o `undefined` explícito para no
   * guardar nada (rebote #164 FIX 1: el texto de un movimiento de cuenta PERSONAL puede traer datos
   * de terceros que sólo yo debía ver).
   */
  recordNotes?: string;
}

export interface MyIncomePayload {
  header: TxHeaderInput;
  lines: TxLineInput[];
  record: Omit<CreateIncomeRecordInput, "source_transaction_id" | "pool_account_id">;
}

/**
 * Un ingreso propio: un movimiento con una línea `private` mía + categoría de ingreso, y el
 * `income_records` que lo respalda. Es lo que hace `IncomeDialog` y lo que carga la conciliación
 * como "Ingreso mío" (una sola definición para los dos lados).
 */
export function buildMyIncomePayload(input: MyIncomeInput): MyIncomePayload {
  const notes = input.notes?.trim() || undefined;
  // `input.recordNotes` explícito en `undefined` (rebote #164 FIX 1) tiene que distinguirse de "no
  // pasó la clave": un objeto literal `{ recordNotes: undefined }` SÍ tiene la clave (a diferencia
  // de omitirla), así que `in` ve la intención aunque el valor sea `undefined`.
  const recordNotes = "recordNotes" in input ? input.recordNotes?.trim() || undefined : notes;
  return {
    header: {
      account_id: input.accountId,
      date: input.date,
      total_amount: input.amount,
      description: notes ?? "Ingreso",
    },
    lines: [
      {
        owner_id: input.memberId,
        amount: input.amount,
        category_id: input.categoryId,
        visibility: "private",
      },
    ],
    record: {
      household_id: input.householdId,
      member_id: input.memberId,
      amount: input.amount,
      currency: input.currency,
      fx_rate_to_base: input.fxRateToBase,
      amount_base: input.amount * input.fxRateToBase,
      base_currency: input.baseCurrency,
      period_year: Number(input.date.slice(0, 4)),
      period_month: Number(input.date.slice(5, 7)),
      notes: recordNotes,
    },
  };
}

/**
 * Crea el ingreso completo (movimiento + registro). Si el registro falla se deshace el
 * movimiento: un depósito sin `income_records` desaparece de "Faltan cargar" y el ingreso nunca
 * llega al hogar, indistinguible de uno bien cargado.
 */
export async function createMyIncome(
  payload: MyIncomePayload,
): Promise<{ transactionId: string; record: IncomeRecord }> {
  const transactionId = await createTransaction(payload.header, payload.lines);
  try {
    const { error } = await supabase
      .from("transaction_headers")
      .update({ is_income_record: true })
      .eq("id", transactionId);
    if (error) throw error;
    const record = await createIncomeRecord({ ...payload.record, source_transaction_id: transactionId });
    return { transactionId, record };
  } catch (error) {
    try {
      await deleteTransaction(transactionId);
    } catch (cleanupError) {
      console.error("No se pudo deshacer el movimiento del ingreso", cleanupError);
    }
    throw error;
  }
}

// ── F5b: pozo de ingresos del hogar ────────────────────────────────────────────

export interface IncomePoolDepositRow {
  id: string;
  account_id: string;
  date: string;
  total_amount: number;
  description: string;
}

/**
 * Depósitos del pozo (movimientos con `purpose = 'household_income_pool'`). Son compartidos:
 * los dos miembros los leen. Un error de lectura se LANZA: leerlo como "no hay depósitos"
 * mostraría todo lo declarado como excedido (APRENDIZAJES #129).
 */
export async function fetchIncomePoolDeposits(householdId: string): Promise<IncomePoolDepositRow[]> {
  const { data, error } = await supabase
    .from("transaction_headers")
    .select("id, account_id, date, total_amount, description")
    .eq("household_id", householdId)
    .eq("purpose", "household_income_pool")
    .order("date", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((h) => ({
    id: h.id,
    account_id: h.account_id,
    date: h.date,
    total_amount: Number(h.total_amount),
    description: h.description ?? "",
  }));
}

/** Declara la parte de `memberId` en el pozo de una cuenta del hogar para un mes. Sin movimiento propio. */
export async function createPoolPart(input: {
  householdId: string;
  memberId: string;
  accountId: string;
  year: number;
  month: number;
  amount: number;
  /** Moneda de la cuenta del pozo = moneda base del hogar (lo exige la DB). */
  currency: string;
}): Promise<IncomeRecord> {
  return createIncomeRecord({
    household_id: input.householdId,
    member_id: input.memberId,
    amount: input.amount,
    currency: input.currency,
    fx_rate_to_base: 1,
    amount_base: input.amount,
    base_currency: input.currency,
    period_year: input.year,
    period_month: input.month,
    pool_account_id: input.accountId,
  });
}

/**
 * Cambia el monto de una parte del pozo ya declarada. Mira `error` Y que el UPDATE haya devuelto fila: la
 * RLS filtra en silencio (una parte ajena da 0 filas sin error). `amount` y `amount_base` van juntos: la
 * DB exige `amount_base = amount` en las partes del pozo.
 */
export async function updatePoolPart(id: string, amount: number): Promise<void> {
  const { data, error } = await supabase
    .from("income_records" as any)
    .update({ amount, amount_base: amount })
    .eq("id", id)
    .select();
  if (error) throw error;
  if (!data || (data as any[]).length === 0) {
    throw new Error("no_permission_update_income");
  }
}

export async function deleteIncomeRecord(id: string): Promise<void> {
  const { data, error } = await supabase
    .from("income_records" as any)
    .delete()
    .eq("id", id)
    .select();
  if (error) throw error;
  if (!data || (data as any[]).length === 0) {
    throw new Error("no_permission_delete_income");
  }
}

export async function deleteIncomeRecordWithTransaction(record: IncomeRecord): Promise<void> {
  await deleteIncomeRecord(record.id);
  if (record.source_type === "manual" && record.source_transaction_id) {
    await deleteTransaction(record.source_transaction_id);
  }
}

export async function recalcHouseholdSplit(householdId: string): Promise<void> {
  const { error } = await supabase.rpc("recalc_household_split" as any, {
    _household_id: householdId,
  });
  if (error) throw error;
}

export async function fetchHouseholdSplitRatio(householdId: string): Promise<HouseholdSplitRatio | null> {
  const { data: hh, error: hhErr } = await supabase
    .from("households")
    .select("split_ratio_a, split_ratio_b, split_ratio_updated_at, income_window_months")
    .eq("id", householdId)
    .maybeSingle();
  if (hhErr) throw hhErr;
  if (!hh) return null;

  const { data: members } = await supabase
    .from("profiles")
    .select("id, created_at")
    .eq("household_id", householdId)
    .order("created_at", { ascending: true });
  const [memA, memB] = members ?? [];

  return {
    split_ratio_a: (hh as any).split_ratio_a ?? null,
    split_ratio_b: (hh as any).split_ratio_b ?? null,
    split_ratio_updated_at: (hh as any).split_ratio_updated_at ?? null,
    income_window_months: (hh as any).income_window_months ?? 3,
    member_a_id: memA?.id ?? null,
    member_b_id: memB?.id ?? null,
  };
}

export async function updateIncomeWindowMonths(householdId: string, months: number): Promise<void> {
  const { error } = await supabase
    .from("households")
    .update({ income_window_months: months } as any)
    .eq("id", householdId);
  if (error) throw error;
}
